import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useLabel from 'src/hooks/useLabel';
import { getSendToDealerData, lgpdSelector } from 'src/redux/app/selectors';
import Label from 'components/_utility/Label';
import Radio from 'components/_form/Radio/Radio';
import Checkbox from 'components/_form/Checkbox/Checkbox';
import {
  Wrap,
  H4,
  Tagline,
  Input,
  Buttons,
  Button,
  InputError,
  Radios,
  Checkboxes,
  CheckboxWrapper,
  Text,
  ChevronDown,
  ContentToggle,
  AdditionalContent,
} from './EmailModal.styled';

const EmailModalSchema = (showAgreeToTerms, isLgpdActive) =>
  Yup.object().shape({
    email: Yup.string()
      .email('invalidEmailLabel')
      .required('invalidEmailLabel'),
    dataConsent: showAgreeToTerms && isLgpdActive ? Yup.bool().oneOf([true], 'dataConsentRequired') : undefined,
    agreeToTerms: showAgreeToTerms ? Yup.bool().oneOf([true], 'agreeToTermsRequired') : undefined,
  });

const EmailModal = ({
  headingLabel,
  isVerticalButtonAlignment,
  primaryActionFunc,
  primaryActionLabel,
  secondaryActionFunc,
  secondaryActionLabel,
  showAgreeToTerms,
  taglineLabel,
}) => {
  const getLabel = useLabel();
  const lgpd = useSelector(lgpdSelector);
  const { privacyPolicy } = useSelector(getSendToDealerData);
  const [showLgpdContent, setShowLgpdContent] = useState(false);

  return (
    <Formik
      initialValues={{
        email: '',
        dataConsent: false,
        dataConsentOptIn: '',
        dataConsentDoNotContact: '',
        receiveCommunication: false,
        agreeToTerms: false,
      }}
      validationSchema={EmailModalSchema(showAgreeToTerms, !!lgpd)}
      onSubmit={values => primaryActionFunc(values.email, values.dataConsentDoNotContact, values.receiveCommunication)}
    >
      {({ values, errors, touched, handleChange, setValues }) => (
        <Form>
          <Wrap>
            <H4>
              <Label name={headingLabel}></Label>
            </H4>
            <Tagline>
              <Label name={taglineLabel}></Label>
            </Tagline>
            <Input
              type="text"
              name="email"
              value={values.email}
              onChange={handleChange}
              placeholder={getLabel('enterEmailLabel')}
              data-testid="emailInput"
            />
            {touched.email && errors.email ? (
              <InputError>
                <Label name={errors.email} />
              </InputError>
            ) : null}
            {showAgreeToTerms && lgpd && (
              <>
                <Text>
                  <Label name="dataConsentLabel"></Label>
                </Text>
                <ContentToggle
                  role="button"
                  onClick={() => setShowLgpdContent(!showLgpdContent)}
                  isActive={showLgpdContent}
                >
                  <Label name="moreContentToggleLabel"></Label>
                  <ChevronDown />
                </ContentToggle>
                <AdditionalContent isActive={showLgpdContent}>
                  <Label name="dataConsentAdditionalContentLabel"></Label>
                </AdditionalContent>
                <Radios>
                  <Radio
                    id="dataConsentOptIn"
                    label="dataConsentOptInLabel"
                    checked={values.dataConsentOptIn}
                    value="dataConsentOptIn"
                    name="dataConsent"
                    onChange={() =>
                      setValues(prevValues => ({
                        ...prevValues,
                        dataConsent: true,
                        dataConsentOptIn: true,
                        dataConsentDoNotContact: false,
                      }))
                    }
                  />
                  <Radio
                    id="dataConsentDoNotContact"
                    label="dataConsentDoNotContactLabel"
                    checked={values.dataConsentDoNotContact}
                    value="dataConsentDoNotContact"
                    name="dataConsent"
                    onChange={() =>
                      setValues(prevValues => ({
                        ...prevValues,
                        dataConsent: true,
                        dataConsentOptIn: false,
                        dataConsentDoNotContact: true,
                      }))
                    }
                  />
                  {touched.dataConsent && errors.dataConsent && (
                    <InputError>
                      <Label name={errors.dataConsent}></Label>
                    </InputError>
                  )}
                </Radios>
                <Checkboxes>
                  {values.dataConsentOptIn && (
                    <CheckboxWrapper>
                      <Checkbox
                        id="receiveCommunication"
                        name="receiveCommunication"
                        checked={values.receiveCommunication}
                        onChange={handleChange}
                      >
                        <Label name="receiveCommunicationConsentLabel"></Label>
                      </Checkbox>
                    </CheckboxWrapper>
                  )}
                  <CheckboxWrapper>
                    <Checkbox
                      id="agreeToTerms"
                      name="agreeToTerms"
                      checked={values.agreeToTerms}
                      onChange={handleChange}
                    >
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: getLabel(
                            'agreeToTermsLabel',
                            'I have read and understand the Triumph {0}, {1}, and {2}'
                          )
                            .replace(
                              '{0}',
                              `<a href="${lgpd.privacyPolicyLink}" target="_blank">${getLabel(
                                'privacyPolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{1}',
                              `<a href="${lgpd.cookiePolicyLink}" target="_blank">${getLabel(
                                'cookiePolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{2}',
                              `<a href="${lgpd.termsAndConditionsLink}" target="_blank">${getLabel(
                                'termsAndConditionsLinkLabel'
                              )}</a>`
                            ),
                        }}
                      />
                    </Checkbox>
                    {touched.agreeToTerms && errors.agreeToTerms && (
                      <InputError>
                        <Label name="agreeToTermsRequiredLGPD"></Label>
                      </InputError>
                    )}
                  </CheckboxWrapper>
                </Checkboxes>
              </>
            )}
            {showAgreeToTerms && !lgpd && (
              <>
                <Checkboxes>
                  <CheckboxWrapper>
                    <Checkbox
                      id="receiveCommunication"
                      name="receiveCommunication"
                      checked={values.receiveCommunication}
                      onChange={handleChange}
                    >
                      {getLabel('receiveCommunicationsLabel')}
                    </Checkbox>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <Checkbox
                      id="agreeToTerms"
                      name="agreeToTerms"
                      checked={values.agreeToTerms}
                      onChange={handleChange}
                    >
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: getLabel(
                            'privacyPolicyDescription',
                            'I have read and understand the Triumph {0}.'
                          ).replace(
                            '{0}',
                            `<a href="${privacyPolicy}" target="_blank">${getLabel('privacyPolicyLabel')}</a>`
                          ),
                        }}
                      />
                    </Checkbox>
                    {touched.agreeToTerms && errors.agreeToTerms && (
                      <InputError>
                        <Label name="agreeToTermsRequired"></Label>
                      </InputError>
                    )}
                  </CheckboxWrapper>
                </Checkboxes>
              </>
            )}
            <Buttons $vertical={isVerticalButtonAlignment}>
              <Button primary data-testid="primaryAction" type="submit" $vertical={isVerticalButtonAlignment}>
                <Label name={primaryActionLabel} />
              </Button>
              <Button onClick={secondaryActionFunc} data-testid="secondaryAction" $vertical={isVerticalButtonAlignment}>
                <Label name={secondaryActionLabel} />
              </Button>
            </Buttons>
          </Wrap>
        </Form>
      )}
    </Formik>
  );
};

export default EmailModal;
